"use client";
/* Claim a creator code: POST /api/creators { handle } (signed in only).
   The server derives the code (letters and digits of the handle, up to 10,
   plus "5": @mia -> MIA5) and stores it; the preview here is cosmetic.
   Her current code comes from GET /api/users/me (creatorCode, "" if none). */
import { useState, type FormEvent } from "react";
import Link from "next/link";
import { useCovet } from "@/components/providers/CovetProvider";
import { useAuth } from "@/components/providers/AuthProvider";
import { useFlows } from "@/components/flows/useFlows";
import { Actions, CloseBtn, Eyebrow, MLink } from "@/components/flows/common";
import { ApiError } from "@/lib/client/api";
import { claimCreatorCode, HANDLE_MIN_LETTERS, handleLetters, previewCode } from "@/lib/client/creators";

const FINE_PRINT = "Codes apply at sign up, for new accounts only. Credit is spendable on any box. Earnings accrue on your referrals' cash purchases for the life of their account, never on credit spend or refunded amounts.";

function friendly(e: unknown): string {
  if (!(e instanceof ApiError)) return "Something went wrong. Please try again.";
  switch (e.code) {
    case "HANDLE_TOO_SHORT": return `Use at least ${HANDLE_MIN_LETTERS} letters or numbers from your name or handle.`;
    case "CODE_TAKEN": return "That code is taken. Try another name or handle.";
    case "ACCOUNT_INACTIVE": return "This account is suspended. Contact support@covet.gg and we'll help.";
    case "TOO_MANY_REQUESTS": return "Too many tries. Please wait a minute and try again.";
    case "VALIDATION_ERROR": return "Use at most 50 characters.";
    default: return e.message || "Something went wrong. Please try again.";
  }
}

export function ClaimCodeCard() {
  const auth = useAuth();
  const f = useFlows();

  if (auth.status === "checking") return <div className="apply-card" aria-busy="true"><p className="micro" style={{ textAlign: "center" }}>One sec, love ♥</p></div>;

  if (auth.status === "signedOut" || !auth.user) {
    return (
      <div className="apply-card">
        <p className="modal-sub" style={{ marginTop: 0 }}>Sign in to claim your code. It takes a minute, and your code works right away.</p>
        <Actions style={{ flexDirection: "column" }}>
          <button className="btn btn-gold" style={{ width: "100%" }} onClick={() => f.openAuth("up")}>Create my account</button>
          <button className="btn btn-quiet" style={{ width: "100%" }} onClick={() => f.openAuth("in")}>I have an account · Sign in</button>
        </Actions>
        <p className="micro" style={{ marginTop: 14, textAlign: "center" }}>{FINE_PRINT}</p>
      </div>
    );
  }

  if (auth.user.creatorCode) return <HasCode code={auth.user.creatorCode} />;
  return <ClaimForm />;
}

function HasCode({ code }: { code: string }) {
  const { modal } = useCovet();
  const copy = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(code).catch(() => {});
    modal.open(<>
      <Eyebrow>✨ Copied</Eyebrow>
      <h2>{code}</h2>
      <p className="modal-sub">Drop it in your bio, your captions, the group chat.</p>
      <Actions><CloseBtn className="btn btn-gold">Done</CloseBtn></Actions>
    </>);
  };
  return (
    <div className="apply-card">
      <div className="field">
        <label>Your creator code</label>
        <div className="claim-code" id="myCreatorCode">{code}</div>
      </div>
      <p className="micro" style={{ textAlign: "center", margin: "0 0 12px" }}>One code per account. It&apos;s live: friends get $5 off their first box.</p>
      <Actions style={{ flexDirection: "column" }}>
        <button className="btn btn-gold" style={{ width: "100%" }} onClick={copy}>Copy my code</button>
        <Link className="btn btn-quiet" style={{ width: "100%" }} href="/profile">See it on my profile</Link>
      </Actions>
    </div>
  );
}

function ClaimForm() {
  const { modal, confetti } = useCovet();
  const auth = useAuth();
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const preview = previewCode(handle);

  const claim = async (ev?: FormEvent) => {
    ev?.preventDefault();
    if (busy) return;
    if (handleLetters(handle).length < HANDLE_MIN_LETTERS) { setError(friendly(new ApiError(400, "HANDLE_TOO_SHORT", ""))); return; }
    setBusy(true);
    setError("");
    try {
      const mine = await claimCreatorCode(handle.trim());
      void auth.reload(); /* profile, header and this card pick up the new code */
      confetti();
      modal.open(<>
        <Eyebrow>✨ It&apos;s live</Eyebrow>
        <h2>{mine.code}</h2>
        <p className="modal-sub">Your code works right now. Drop it in your bio and captions: friends get $5 off their first box, and you earn credit on every box they ever open. Track it all on your profile.</p>
        <Actions>
          <MLink className="btn btn-gold" href="/profile">See my profile</MLink>
          <CloseBtn>Done</CloseBtn>
        </Actions>
      </>);
    } catch (e) {
      setBusy(false);
      if (e instanceof ApiError && e.code === "ALREADY_CREATOR") { void auth.reload(); return; } /* show the code she has */
      setError(friendly(e));
    }
  };

  return (
    <form className="apply-card" onSubmit={claim} noValidate>
      <div className="field">
        <label htmlFor="claimHandle">Your name or handle</label>
        <input id="claimHandle" type="text" placeholder="mia" maxLength={50} autoComplete="off" value={handle}
          onChange={(e) => { setHandle(e.target.value); setError(""); }} aria-invalid={!!error} aria-describedby="claimHint" />
        <p className="micro" id="claimHint" style={{ textAlign: "left", margin: "6px 0 0" }} aria-live="polite">
          {preview ? <>Your code will be <b style={{ color: "var(--ink)" }}>{preview}</b></> : `At least ${HANDLE_MIN_LETTERS} letters or numbers. @mia becomes MIA5.`}
        </p>
        {error && <p className="field-err" role="alert">{error}</p>}
      </div>
      <button type="submit" className="btn btn-gold" style={{ width: "100%", marginTop: 8 }} disabled={busy} aria-busy={busy}>{busy ? "Claiming…" : "Claim my code"}</button>
      <p className="micro" style={{ marginTop: 14, textAlign: "center" }}>{FINE_PRINT}</p>
    </form>
  );
}
